// pages/routing/RoutingPropose.jsx — SOUND-827 Phase 2 (propose mode)
//
// The editable bodies for /routing/:companyId while "Propose changes" is on,
// plus the draft tray. NOTHING here writes to CheckoutChamp: edits accumulate in
// a client-side draft (one change per field) and "Submit proposal" posts a
// change set for an internal admin to tick and GO on the review page.
//
// All draft logic — one change per field, add/remove, the disable-zeroes-share
// rule, resulting totals and the sum-to-100 flag — is in utilities/routingChanges
// (tested). These components only render it.
import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Info, Plus, Trash2, Undo2, ChevronDown, ChevronRight } from "lucide-react";
import {
  formatValue,
  formatPercent,
  resolveMid,
  sortByPriority,
  isGatewayEnabled,
  itemId,
  toNumber,
  objectTypeLabel,
} from "utilities/routingView";
import {
  ROUTING_ALGORITHMS,
  GATEWAY_LIMIT_TYPES,
  editableFields,
  fieldLabel,
  setFieldEdit,
  addGatewayEdit,
  removeGatewayEdit,
  findFieldChange,
  findAdd,
  coerceInput,
  flagValue,
  validateChange,
  changeNote,
  applyChangesToItem,
  resultingSummary,
  changeLabel,
  formatChangeFromTo,
  objectTitle,
  sumProblemText,
} from "utilities/routingChanges";
import { MidLabel, MiniTable, Fact, td, useShowMore } from "./RoutingTabs";

const PAGE = 50;

// ---------------------------------------------------------------------------
// Field controls
// ---------------------------------------------------------------------------

/**
 * A text input that commits on blur / Enter. Committing per keystroke would
 * coerce "4." to 4 mid-typing (coerceInput keeps CC's number type), so the text
 * is local until the user is done.
 */
function CommitInput({ value, onCommit, changed, invalid, disabled, align = "left", width = "w-full", ariaLabel }) {
  const shown = value === null || value === undefined ? "" : String(value);
  const [text, setText] = useState(shown);
  const [editing, setEditing] = useState(false);
  useEffect(() => {
    if (!editing) setText(shown);
  }, [shown, editing]);
  return (
    <input
      type="text"
      aria-label={ariaLabel}
      className={`input h-8 px-2 py-1 text-[13px] ${width} ${align === "right" ? "text-right" : ""} ${
        invalid ? "ring-1 ring-destructive" : changed ? "ring-1 ring-warning" : ""
      }`}
      value={text}
      disabled={disabled}
      onFocus={() => setEditing(true)}
      onChange={(e) => setText(e.target.value)}
      onBlur={() => {
        setEditing(false);
        if (text !== shown) onCommit(text);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") e.currentTarget.blur();
        if (e.key === "Escape") {
          setText(shown);
          setEditing(false);
          e.currentTarget.blur();
        }
      }}
    />
  );
}

/** A select over `options`, always including the current value even if CC sent an unlisted one. */
function ValueSelect({ value, options, onChange, changed, ariaLabel, blankLabel }) {
  const v = value === null || value === undefined ? "" : String(value);
  const opts = [...options];
  if (v !== "" && !opts.includes(v)) opts.unshift(v);
  return (
    <select
      aria-label={ariaLabel}
      className={`select h-8 w-full py-1 text-[13px] ${changed ? "ring-1 ring-warning" : ""}`}
      value={v}
      onChange={(e) => onChange(e.target.value)}
    >
      {v === "" && <option value="">{blankLabel || "—"}</option>}
      {opts.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  );
}

function FlagSelect({ value, live, onChange, changed, ariaLabel }) {
  const on = toNumber(value) !== 0 && String(value).toLowerCase() !== "false";
  return (
    <select
      aria-label={ariaLabel}
      className={`select h-8 w-full py-1 text-[13px] ${changed ? "ring-1 ring-warning" : ""}`}
      value={on ? "1" : "0"}
      onChange={(e) => onChange(flagValue(live, e.target.value === "1"))}
    >
      <option value="1">Yes</option>
      <option value="0">No</option>
    </select>
  );
}

/**
 * One object-level field. `kind` picks the control; the value shown is the
 * draft's `to` when there is one, else the live value.
 */
function ObjectField({ objectType, item, field, kind = "text", draft, onDraft }) {
  const ccId = itemId(objectType, item);
  const live = item[field];
  const change = findFieldChange(draft, { objectType, ccId, field });
  const value = change ? change.to : live;
  const commit = (to) => onDraft(setFieldEdit(draft, { objectType, ccId, field, from: live, to }));
  const err = change ? validateChange(change) : null;

  let control;
  if (kind === "algorithm") {
    control = (
      <ValueSelect ariaLabel={fieldLabel(field)} value={value} options={ROUTING_ALGORITHMS} onChange={commit} changed={!!change} />
    );
  } else if (kind === "flag") {
    control = <FlagSelect ariaLabel={fieldLabel(field)} value={value} live={live} onChange={commit} changed={!!change} />;
  } else {
    control = (
      <CommitInput
        ariaLabel={fieldLabel(field)}
        value={value}
        changed={!!change}
        invalid={!!err}
        onCommit={(raw) => commit(coerceInput(live, raw))}
      />
    );
  }
  return (
    <Fact label={fieldLabel(field)}>
      <div className="whitespace-normal">
        {control}
        {change && (
          <div className="mt-0.5 text-[11px] text-muted-foreground">was {formatValue(live)}</div>
        )}
        {err && <div className="mt-0.5 text-[11px] text-destructive">{err}</div>}
      </div>
    </Fact>
  );
}

// ---------------------------------------------------------------------------
// Profiles (the verified surface)
// ---------------------------------------------------------------------------

const PROFILE_PRIMARY = [
  ["enabled", "flag"],
  ["algorithm", "algorithm"],
  ["calcInterval", "text"],
  ["maxDeclines", "text"],
  ["maxHardDeclines", "text"],
  ["limitType", "text"],
];

function MoreFields({ objectType, item, exclude, draft, onDraft, label = "More fields" }) {
  const fields = editableFields(objectType, item).filter((f) => !exclude.includes(f));
  const anyChanged = fields.some((f) => findFieldChange(draft, { objectType, ccId: itemId(objectType, item), field: f }));
  const [open, setOpen] = useState(anyChanged);
  if (!fields.length) return null;
  return (
    <div>
      <button
        type="button"
        className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
        onClick={() => setOpen((o) => !o)}
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        {label} ({fields.length})
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {fields.map((f) => (
            <ObjectField key={f} objectType={objectType} item={item} field={f} draft={draft} onDraft={onDraft} />
          ))}
        </div>
      )}
    </div>
  );
}

function RowCell({ objectType, ccId, row, field, draft, onDraft, disabled }) {
  const billerId = String(row.billerId);
  const adding = !!findAdd(draft, { objectType, ccId, billerId });
  const change = adding ? null : findFieldChange(draft, { objectType, ccId, billerId, field });
  const live = adding ? undefined : row._live?.[field];
  const value = row[field];
  const commit = (to) => onDraft(setFieldEdit(draft, { objectType, ccId, billerId, field, from: live, to }));
  const changed = !!change || (adding && value !== undefined);
  const err = change ? validateChange(change) : adding ? validateChange({ op: "gateway.update", field, to: value }) : null;

  if (field === "enabled") {
    if (adding) return <span className="text-xs text-muted-foreground">On add</span>;
    return <FlagSelect ariaLabel={`${field} ${billerId}`} value={value} live={live} onChange={commit} changed={changed} />;
  }
  if (field === "limitType") {
    return (
      <ValueSelect
        ariaLabel={`${field} ${billerId}`}
        value={value}
        options={GATEWAY_LIMIT_TYPES}
        onChange={commit}
        changed={changed}
        blankLabel="Default"
      />
    );
  }
  return (
    <div>
      <CommitInput
        ariaLabel={`${field} ${billerId}`}
        value={value}
        align="right"
        changed={changed}
        invalid={!!err && !(adding && value === undefined)}
        disabled={disabled}
        onCommit={(raw) => commit(coerceInput(adding ? 0 : live, raw))}
      />
      {err && !(adding && value === undefined) && <div className="mt-0.5 text-[11px] text-destructive">{err}</div>}
    </div>
  );
}

function AddMid({ objectType, item, gateways, resultRows, draft, onDraft }) {
  const [pick, setPick] = useState("");
  const onIt = new Set(resultRows.filter((r) => r._draft !== "removed").map((r) => String(r.billerId)));
  const options = (gateways || []).filter((g) => !onIt.has(String(g.billerId)));
  if (!gateways) {
    return (
      <p className="text-xs text-muted-foreground">
        Gateways aren't readable for this client, so there is no list to add a MID from.
      </p>
    );
  }
  const nextPriority =
    resultRows.reduce((m, r) => Math.max(m, toNumber(r.priority) || 0), 0) + 1;
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select
        aria-label="MID to add"
        className="select h-8 w-auto min-w-[240px] py-1 text-[13px]"
        value={pick}
        onChange={(e) => setPick(e.target.value)}
      >
        <option value="">Add a MID…</option>
        {options.map((g) => (
          <option key={g.billerId} value={String(g.billerId)}>
            {resolveMid(g, g.billerId, g.title)?.label || `#${g.billerId}`}
            {String(g.enabled) === "0" ? " (disabled gateway)" : ""}
          </option>
        ))}
      </select>
      <button
        type="button"
        className="btn btn-outline h-8"
        disabled={!pick}
        onClick={() => {
          onDraft(
            addGatewayEdit(draft, {
              objectType,
              ccId: itemId(objectType, item),
              billerId: pick,
              values: { percentage: 0, priority: nextPriority },
            })
          );
          setPick("");
        }}
      >
        <Plus className="h-4 w-4" />
        Add MID
      </button>
    </div>
  );
}

function SumFooter({ summary, colSpan }) {
  return (
    <tr>
      <td className={`${td} font-medium`}>Total (enabled, after changes)</td>
      <td className={`${td} text-right font-medium`}>
        <span className={summary.flagged ? "text-warning" : ""}>{formatPercent(summary.total)}</span>
      </td>
      <td className={td} colSpan={colSpan}>
        {summary.flagged ? (
          <span className="inline-flex items-center gap-1 text-xs text-warning">
            <AlertTriangle className="h-3.5 w-3.5" />
            WEIGHTED must sum to 100% — CheckoutChamp will not check this, so GO will refuse it
          </span>
        ) : summary.weighted ? (
          <span className="text-xs text-success">Sums to 100%</span>
        ) : null}
      </td>
    </tr>
  );
}

function EditableProfileCard({ profile, gateways, draft, onDraft }) {
  const objectType = "profiles";
  const ccId = itemId(objectType, profile);
  const liveById = useMemo(() => {
    const m = {};
    for (const g of profile.gateways || []) m[String(g.billerId).trim()] = g;
    return m;
  }, [profile.gateways]);
  const result = applyChangesToItem(objectType, profile, draft);
  const rows = sortByPriority(result.gateways || []).map((r) => ({ ...r, _live: liveById[String(r.billerId)] }));
  const summary = resultingSummary(objectType, profile, draft);
  const touched = draft.some((c) => c.objectType === objectType && c.ccId === ccId);

  return (
    <div className={`card p-5 space-y-4 ${touched ? "ring-1 ring-warning/50" : ""}`}>
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-base font-semibold text-foreground">{formatValue(profile.title)}</h3>
        <span className="text-xs text-muted-foreground">#{ccId}</span>
        {touched && <span className="badge badge-warning">Edited</span>}
        {summary.flagged && (
          <span className="badge badge-warning">
            <AlertTriangle className="h-3 w-3" />
            Would sum to {formatPercent(summary.total)}
          </span>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
        {PROFILE_PRIMARY.filter(([f]) => Object.prototype.hasOwnProperty.call(profile, f)).map(([f, kind]) => (
          <ObjectField key={f} objectType={objectType} item={profile} field={f} kind={kind} draft={draft} onDraft={onDraft} />
        ))}
      </div>
      <MoreFields
        objectType={objectType}
        item={profile}
        exclude={PROFILE_PRIMARY.map(([f]) => f)}
        draft={draft}
        onDraft={onDraft}
      />

      {rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No gateways in this profile.</p>
      ) : (
        <MiniTable
          headers={[
            { label: "MID" },
            { label: "%", align: "right" },
            { label: "Priority", align: "right" },
            { label: "Enabled" },
            { label: "Limit type" },
            { label: "Sales limit", align: "right" },
            { label: "Profile cap", align: "right" },
            { label: "" },
          ]}
          footer={<SumFooter summary={summary} colSpan={6} />}
        >
          {rows.map((r) => {
            const billerId = String(r.billerId);
            const removed = r._draft === "removed";
            const added = r._draft === "added";
            const enabledChange = findFieldChange(draft, { objectType, ccId, billerId, field: "enabled" });
            const note = enabledChange ? changeNote(enabledChange, draft) : null;
            const disabledNow = !added && !isGatewayEnabled(r);
            const midTitle = r.title || gateways?.find((g) => String(g.billerId) === billerId)?.title;
            return (
              <React.Fragment key={billerId}>
                <tr
                  className={
                    removed ? "bg-destructive/5 opacity-60" : added ? "bg-success/5" : disabledNow ? "opacity-70" : ""
                  }
                >
                  <td className={`${td} ${removed ? "line-through" : ""}`}>
                    <MidLabel mid={resolveMid(profile, billerId, midTitle)} />
                    {added && <span className="badge badge-success ml-2">Adding</span>}
                    {removed && <span className="badge badge-destructive ml-2">Removing</span>}
                  </td>
                  {removed ? (
                    <td className={td} colSpan={6}>
                      <span className="text-xs text-muted-foreground">Removed from this profile in the draft</span>
                    </td>
                  ) : (
                    <>
                      <td className={`${td} w-24`}>
                        <RowCell
                          objectType={objectType}
                          ccId={ccId}
                          row={r}
                          field="percentage"
                          draft={draft}
                          onDraft={onDraft}
                          disabled={disabledNow}
                        />
                      </td>
                      <td className={`${td} w-20`}>
                        <RowCell objectType={objectType} ccId={ccId} row={r} field="priority" draft={draft} onDraft={onDraft} />
                      </td>
                      <td className={`${td} w-24`}>
                        <RowCell objectType={objectType} ccId={ccId} row={r} field="enabled" draft={draft} onDraft={onDraft} />
                      </td>
                      <td className={`${td} w-28`}>
                        <RowCell objectType={objectType} ccId={ccId} row={r} field="limitType" draft={draft} onDraft={onDraft} />
                      </td>
                      <td className={`${td} w-24`}>
                        <RowCell objectType={objectType} ccId={ccId} row={r} field="salesLimit" draft={draft} onDraft={onDraft} />
                      </td>
                      <td className={`${td} w-24`}>
                        <RowCell objectType={objectType} ccId={ccId} row={r} field="profileCap" draft={draft} onDraft={onDraft} />
                      </td>
                    </>
                  )}
                  <td className={`${td} w-24 text-right`}>
                    {removed ? (
                      <button
                        type="button"
                        className="btn btn-ghost h-8 px-2"
                        onClick={() => onDraft(addGatewayEdit(draft, { objectType, ccId, billerId }))}
                        title="Undo the removal"
                      >
                        <Undo2 className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-ghost h-8 px-2 text-destructive"
                        onClick={() =>
                          onDraft(removeGatewayEdit(draft, { objectType, ccId, billerId, row: r._live || r }))
                        }
                        title={added ? "Drop this add" : "Remove this MID from the profile"}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
                {note && (
                  <tr>
                    <td className={`${td} pt-0`} colSpan={8}>
                      <span className="inline-flex items-center gap-1 text-xs text-warning">
                        <Info className="h-3.5 w-3.5" />
                        {note}
                      </span>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </MiniTable>
      )}
      <AddMid
        objectType={objectType}
        item={profile}
        gateways={gateways}
        resultRows={rows}
        draft={draft}
        onDraft={onDraft}
      />
    </div>
  );
}

/** `gateways` = the client's gateway list (for Add MID), or null when unreadable. */
export function EditableProfilesTab({ items, gateways, draft, onDraft }) {
  const { limit, more } = useShowMore(items.length, PAGE);
  return (
    <div className="space-y-4">
      {items.slice(0, limit).map((p, i) => (
        <EditableProfileCard key={itemId("profiles", p) || i} profile={p} gateways={gateways} draft={draft} onDraft={onDraft} />
      ))}
      {more}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Every other object type: a generic scalar editor (proposable, NOT verified)
// ---------------------------------------------------------------------------

function GenericItem({ objectType, item, draft, onDraft }) {
  const ccId = itemId(objectType, item);
  const touched = draft.some((c) => c.objectType === objectType && c.ccId === ccId);
  return (
    <div className={`card p-4 space-y-2 ${touched ? "ring-1 ring-warning/50" : ""}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-foreground">{objectTitle(objectType, item, ccId)}</span>
        <span className="text-xs text-muted-foreground">#{ccId}</span>
        {touched && <span className="badge badge-warning">Edited</span>}
      </div>
      <MoreFields objectType={objectType} item={item} exclude={[]} draft={draft} onDraft={onDraft} label="Edit fields" />
    </div>
  );
}

export function GenericProposeTab({ objectType, items, filtered, draft, onDraft }) {
  const { limit, more } = useShowMore(items.length, PAGE);
  return (
    <div className="space-y-3">
      <div className="flex items-start gap-2 rounded-lg border border-info/30 bg-info/10 px-4 py-2 text-sm">
        <Info className="mt-0.5 h-4 w-4 shrink-0 text-info" />
        <span>
          {objectTypeLabel(objectType)} changes can be proposed and reviewed, but are not yet verified against
          CheckoutChamp — GO will refuse them until that write has been tested on a test object.
          {objectType === "cascades" ? " Cascade MID rows are not editable here yet." : ""}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="surface py-10 text-center text-sm text-muted-foreground">
          {filtered ? "Nothing matches this search." : "Nothing configured."}
        </div>
      ) : (
        items.slice(0, limit).map((it, i) => (
          <GenericItem key={itemId(objectType, it) || i} objectType={objectType} item={it} draft={draft} onDraft={onDraft} />
        ))
      )}
      {more}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Draft tray
// ---------------------------------------------------------------------------

export function DraftTray({ draft, snapshot, sumProblems, onUndo, onDiscard, onSubmit, submitting }) {
  const [open, setOpen] = useState(true);
  const invalid = draft.filter((c) => validateChange(c));
  return (
    <div className="sticky bottom-0 z-20 -mx-1 pb-2">
      <div className="card border border-warning/40 p-4 shadow-lg">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            className="inline-flex items-center gap-1 text-sm font-medium text-foreground"
            onClick={() => setOpen((o) => !o)}
          >
            {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Draft · {draft.length} {draft.length === 1 ? "change" : "changes"}
            {sumProblems.length > 0 && (
              <span className="badge badge-warning ml-2">
                <AlertTriangle className="h-3 w-3" />
                {sumProblems.length} not summing to 100%
              </span>
            )}
            {invalid.length > 0 && <span className="badge badge-destructive ml-2">{invalid.length} invalid</span>}
          </button>
          <div className="flex items-center gap-2">
            <button type="button" className="btn btn-ghost" onClick={onDiscard} disabled={!draft.length || submitting}>
              Discard draft
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={onSubmit}
              disabled={!draft.length || invalid.length > 0 || submitting}
              title={invalid.length ? "Fix the invalid values first" : undefined}
            >
              Submit proposal
            </button>
          </div>
        </div>

        {open && (
          <div className="mt-3 space-y-2">
            {sumProblems.map((p) => (
              <div key={`${p.objectType}|${p.ccId}`} className="flex items-start gap-2 text-xs text-warning">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>{sumProblemText(p)}. You can still submit — an approver can untick changes — but GO refuses a set that doesn't sum.</span>
              </div>
            ))}
            {draft.length === 0 ? (
              <p className="text-sm text-muted-foreground">No changes yet — edit a field to start a draft.</p>
            ) : (
              <ol className="max-h-64 divide-y divide-border overflow-y-auto rounded-lg border border-border">
                {draft.map((c, i) => {
                  const { from, to } = formatChangeFromTo(c);
                  const note = changeNote(c, draft);
                  const err = validateChange(c);
                  return (
                    <li key={c.key} className="flex items-start justify-between gap-3 px-3 py-2 text-[13px]">
                      <div className="min-w-0">
                        <div className="truncate font-medium text-foreground">
                          <span className="mr-2 text-muted-foreground">{i + 1}.</span>
                          {changeLabel(c, snapshot)}
                        </div>
                        <div className="text-muted-foreground">
                          {from} → <span className="text-foreground">{to}</span>
                        </div>
                        {note && <div className="text-xs text-warning">{note}</div>}
                        {err && <div className="text-xs text-destructive">{err}</div>}
                      </div>
                      <button
                        type="button"
                        className="btn btn-ghost h-8 shrink-0 px-2"
                        onClick={() => onUndo(c.key)}
                        title="Undo this change"
                        disabled={submitting}
                      >
                        <Undo2 className="h-4 w-4" />
                      </button>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
